'use client';

import { useCallback, useEffect, useRef, useState } from 'react';

export interface SelectionBox {
  startX: number;
  startY: number;
  endX: number;
  endY: number;
  isActive: boolean;
}

export interface UseSelectionBoxProps {
  onSelectionChange: (selectedIds: string[]) => void;
  disabled?: boolean;
}

export function useSelectionBox({ onSelectionChange, disabled = false }: UseSelectionBoxProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  // 添加容器矩形缓存
  const containerRectRef = useRef<DOMRect | null>(null);
  // rAF 节流
  const rafIdRef = useRef<number | null>(null);
  const lastMouseClientPosRef = useRef<{ x: number; y: number } | null>(null);
  // 缓存上一次已通知的选中集合，避免重复回调
  const lastSelectedIdsRef = useRef<string[]>([]);

  const DRAG_ACTIVATE_THRESHOLD = 5;

  const [selectionBox, setSelectionBox] = useState<SelectionBox>({
    startX: 0,
    startY: 0,
    endX: 0,
    endY: 0,
    isActive: false,
  });

  const [isSelecting, setIsSelecting] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const startPosRef = useRef({ x: 0, y: 0 });

  // 获取最新的容器矩形信息
  const getContainerRect = useCallback(() => {
    if (!containerRef.current) return null;
    const rect = containerRef.current.getBoundingClientRect();
    containerRectRef.current = rect;
    return rect;
  }, []);

  // 计算选择框与元素的交集
  const getIntersectingElements = useCallback(
    (boxToCheck?: SelectionBox) => {
      const targetBox = boxToCheck || selectionBox;
      if (!containerRef.current || !targetBox.isActive) return [];

      const containerRect = getContainerRect();
      if (!containerRect) return [];

      // 计算选择框的实际坐标
      const boxRect = {
        left: Math.min(targetBox.startX, targetBox.endX),
        top: Math.min(targetBox.startY, targetBox.endY),
        right: Math.max(targetBox.startX, targetBox.endX),
        bottom: Math.max(targetBox.startY, targetBox.endY),
      };

      // 查找所有具有 data-item-id 属性的元素
      const itemElements = containerRef.current.querySelectorAll('[data-item-id]');
      const intersectingIds: string[] = [];

      itemElements.forEach((element) => {
        const rect = element.getBoundingClientRect();
        const itemId = element.getAttribute('data-item-id');

        if (!itemId) return;

        // 将元素坐标转换为相对于容器的坐标
        const elementRect = {
          left: rect.left - containerRect.left,
          top: rect.top - containerRect.top,
          right: rect.right - containerRect.left,
          bottom: rect.bottom - containerRect.top,
        };

        // 检查是否有交集
        const hasIntersection = !(
          boxRect.right < elementRect.left ||
          boxRect.left > elementRect.right ||
          boxRect.bottom < elementRect.top ||
          boxRect.top > elementRect.bottom
        );

        if (hasIntersection) {
          intersectingIds.push(itemId);
        }
      });

      return intersectingIds;
    },
    [selectionBox, getContainerRect],
  );

  // 处理鼠标按下事件
  const handleMouseDown = useCallback(
    (event: MouseEvent) => {
      // 只响应左键点击
      if (event.button !== 0) return;

      const target = event.target as HTMLElement;

      // 如果有 data-no-selection 属性，则不启动框选
      const noSelectionElement = target.closest('[data-no-selection]');
      if (noSelectionElement) return;

      // 获取最新的容器矩形信息
      const containerRect = getContainerRect();
      if (!containerRect) return;

      // 检查是否点击在容器内
      const isInContainer =
        event.clientX >= containerRect.left &&
        event.clientX <= containerRect.right &&
        event.clientY >= containerRect.top &&
        event.clientY <= containerRect.bottom;

      if (!isInContainer) return;

      event.preventDefault();

      const startX = event.clientX - containerRect.left;
      const startY = event.clientY - containerRect.top;

      startPosRef.current = { x: startX, y: startY };
      setIsSelecting(true);
      setSelectionBox({
        startX,
        startY,
        endX: startX,
        endY: startY,
        isActive: false,
      });
    },
    [disabled, getContainerRect],
  );

  // 处理鼠标移动事件
  const handleMouseMove = useCallback(
    (event: MouseEvent) => {
      if (!isSelecting) return;

      // 记录最新的鼠标位置
      lastMouseClientPosRef.current = { x: event.clientX, y: event.clientY };

      if (rafIdRef.current !== null) return; // 已经有一帧在排队

      rafIdRef.current = window.requestAnimationFrame(() => {
        rafIdRef.current = null;

        // 始终读取最新的容器矩形，避免滚动导致的相对位移误差
        const containerRect = getContainerRect();
        if (!containerRect || !lastMouseClientPosRef.current) return;

        const currentX = lastMouseClientPosRef.current.x - containerRect.left;
        const currentY = lastMouseClientPosRef.current.y - containerRect.top;

        // 检查是否移动了足够的距离来启动拉框选择
        const deltaX = Math.abs(currentX - startPosRef.current.x);
        const deltaY = Math.abs(currentY - startPosRef.current.y);
        const exceeded = deltaX > DRAG_ACTIVATE_THRESHOLD || deltaY > DRAG_ACTIVATE_THRESHOLD;

        if (!isDragging && exceeded) {
          setIsDragging(true);
        }

        // 激活后持续更新；未激活但超过阈值时也更新一次以显示
        if (isDragging || exceeded) {
          const newSelectionBox: SelectionBox = {
            startX: startPosRef.current.x,
            startY: startPosRef.current.y,
            endX: currentX,
            endY: currentY,
            isActive: true,
          };

          setSelectionBox(newSelectionBox);

          // 计算交集并仅在变化时回调
          const selectedIds = getIntersectingElements(newSelectionBox);

          const last = lastSelectedIdsRef.current;
          const hasChanged = (() => {
            if (last.length !== selectedIds.length) return true;
            if (last.length === 0) return false;
            const setB = new Set(selectedIds);
            for (const id of last) {
              if (!setB.has(id)) return true;
            }
            return false;
          })();

          if (hasChanged) {
            lastSelectedIdsRef.current = selectedIds;
            onSelectionChange(selectedIds);
          }
        }
      });
    },
    [isSelecting, isDragging, onSelectionChange, getContainerRect, getIntersectingElements],
  );

  // 处理鼠标释放事件
  const handleMouseUp = useCallback(() => {
    if (!isSelecting) return;

    // 如果没有拖拽，才清除选择
    if (!isDragging) {
      onSelectionChange([]);
    }
    // 如果有拖拽，选择状态已经在 handleMouseMove 中实时更新了

    // 重置状态
    setIsSelecting(false);
    setIsDragging(false);
    setSelectionBox((prev) => ({ ...prev, isActive: false }));
    // 清除容器矩形缓存
    containerRectRef.current = null;
    // 清理 rAF
    if (rafIdRef.current !== null) {
      cancelAnimationFrame(rafIdRef.current);
      rafIdRef.current = null;
    }
    lastMouseClientPosRef.current = null;
    lastSelectedIdsRef.current = [];
  }, [isSelecting, isDragging, onSelectionChange]);

  // 监听容器变化，重新计算坐标
  useEffect(() => {
    if (!containerRef.current) return;

    const container = containerRef.current;
    const resizeObserver = new ResizeObserver(() => {
      // 容器大小变化时清除缓存
      containerRectRef.current = null;
    });

    resizeObserver.observe(container);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  // 组件卸载时的兜底清理
  useEffect(() => {
    return () => {
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = null;
      }
    };
  }, []);

  // 注册事件监听器
  useEffect(() => {
    if (disabled || !containerRef.current) return;

    containerRef.current.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      containerRef.current?.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [handleMouseDown, handleMouseMove, handleMouseUp, disabled]);

  return {
    selectionBox,
    containerRef,
    isSelecting: isSelecting && isDragging,
  };
}
